import { clientPromiseEffect } from "@crm/effect/client/promise-effect";
import { browserEffectRuntime } from "@crm/effect/client/runtime";
import type { AppError } from "@crm/errors/index";
import { unsafeDateTimeInputToUtcDate } from "@crm/shared/time/beijing-time";
import { InfiniteScrollTrigger, ListPageShell, PageHeader } from "@crm/ui/components/shared";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@crm/ui/components/ui/alert-dialog";
import { Badge } from "@crm/ui/components/ui/badge";
import { Button } from "@crm/ui/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@crm/ui/components/ui/card";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { Effect } from "effect";
import { AlertTriangle, Check, Loader2, RefreshCw, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
  useCheckEntryDuplicates,
  useDeleteKnowledgeEntry,
  useReviewKnowledgeEntry,
} from "#app/knowledge/knowledge.mutations";
import { useKnowledgeEntriesInfinite } from "#app/knowledge/knowledge.queries";
import { KnowledgeNav } from "#components/knowledge";

function promiseEffect<A>(run: () => PromiseLike<A>) {
  return clientPromiseEffect("知识审核操作失败", run);
}

function runKnowledgeReviewRouteEffect<A>(effect: Effect.Effect<A, AppError>): Promise<A> {
  return browserEffectRuntime.runPromise(effect);
}

function fallbackErrorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

export const Route = createFileRoute("/_protected/knowledge/review")({
  head: () => ({
  }),
  component: ReviewPage,
});

type DuplicateInfo = {
  id: string;
  title: string;
  sourceMessage: string | null;
  status: string;
  score: number;
};

type DuplicateCheckResult = {
  entryId: string;
  entryTitle: string;
  hasDuplicates: boolean;
  duplicates: DuplicateInfo[];
};

function removeDuplicateFromResults(
  prev: Record<string, DuplicateCheckResult>,
  parentEntryId: string,
  duplicateId: string,
) {
  const result = prev[parentEntryId];
  if (!result) {
    return prev;
  }

  const duplicates = result.duplicates.filter((d) => d.id !== duplicateId);
  return {
    ...prev,
    [parentEntryId]: {
      ...result,
      duplicates,
      hasDuplicates: duplicates.length > 0,
    },
  };
}

function ReviewPage() {
  // 状态映射
  const getStatusLabel = (status: string) => {
    const statusMap: Record<
      string,
      {
        label: string;
        variant: "default" | "secondary" | "outline" | "destructive";
      }
    > = {
      pending_review: { label: "待审核", variant: "secondary" },
      approved: { label: "审核通过", variant: "outline" },
      draft: { label: "草稿", variant: "secondary" },
      published: { label: "已发布", variant: "default" },
    };
    return statusMap[status] || { label: status, variant: "secondary" };
  };

  const [checkingDuplicates, setCheckingDuplicates] = useState<string | null>(null);
  // 存储每个条目的查重结果
  const [duplicateResults, setDuplicateResults] = useState<Record<string, DuplicateCheckResult>>(
    {},
  );
  const [approveEntry, setApproveEntry] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [deleteEntry, setDeleteEntry] = useState<{
    id: string;
    title: string;
  } | null>(null);
  // 相似条目的通过/删除确认
  const [approveDuplicate, setApproveDuplicate] = useState<{
    id: string;
    title: string;
    parentEntryId: string;
  } | null>(null);
  const [deleteDuplicate, setDeleteDuplicate] = useState<{
    id: string;
    title: string;
    parentEntryId: string;
  } | null>(null);

  // Mutations
  const reviewEntryMutation = useReviewKnowledgeEntry();
  const deleteEntryMutation = useDeleteKnowledgeEntry();
  const checkDuplicatesMutation = useCheckEntryDuplicates();

  // 获取待审核条目（分页 + infinite scroll）
  const {
    items: pendingEntries,
    totalCount,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error: entriesError,
  } = useKnowledgeEntriesInfinite({ status: "pending_review" });

  const handleCheckDuplicate = (entryId: string) => {
    if (checkingDuplicates) {
      return;
    }

    setCheckingDuplicates(entryId);
    void runKnowledgeReviewRouteEffect(
      promiseEffect(() => checkDuplicatesMutation.executePromise(entryId)),
    )
      .then((result) => {
        setDuplicateResults((prev) => ({ ...prev, [entryId]: result }));
      })
      .catch((cause) => {
        toast.error(fallbackErrorMessage(cause, "查重失败"));
      })
      .finally(() => {
        setCheckingDuplicates(null);
      });
  };

  const handleApprove = () => {
    const entry = approveEntry;
    if (!entry) {
      return;
    }

    void runKnowledgeReviewRouteEffect(
      promiseEffect(() =>
        reviewEntryMutation.executePromise({
          entryId: entry.id,
          action: "approve",
        }),
      ),
    )
      .then(() => {
        toast.success("审核通过");
        setApproveEntry(null);
      })
      .catch(() => {
        toast.error("操作失败");
      });
  };

  const handleDelete = () => {
    const entry = deleteEntry;
    if (!entry) {
      return;
    }

    void runKnowledgeReviewRouteEffect(
      promiseEffect(() =>
        reviewEntryMutation.executePromise({
          entryId: entry.id,
          action: "delete",
        }),
      ),
    )
      .then(() => {
        toast.success("已删除");
        setDeleteEntry(null);
      })
      .catch(() => {
        toast.error("删除失败");
      });
  };

  // 处理相似条目通过
  const handleApproveDuplicate = () => {
    const duplicate = approveDuplicate;
    if (!duplicate) {
      return;
    }

    void runKnowledgeReviewRouteEffect(
      promiseEffect(() =>
        reviewEntryMutation.executePromise({
          entryId: duplicate.id,
          action: "approve",
        }),
      ),
    )
      .then(() => {
        // 从查重结果中移除该条目
        setDuplicateResults((prev) =>
          removeDuplicateFromResults(prev, duplicate.parentEntryId, duplicate.id),
        );

        toast.success(`已通过「${duplicate.title}」`);
        setApproveDuplicate(null);
      })
      .catch(() => {
        toast.error("操作失败");
      });
  };

  // 处理相似条目删除
  const handleDeleteDuplicate = () => {
    const duplicate = deleteDuplicate;
    if (!duplicate) {
      return;
    }

    void runKnowledgeReviewRouteEffect(
      promiseEffect(() => deleteEntryMutation.executePromise(duplicate.id)),
    )
      .then(() => {
        // 从查重结果中移除该条目
        setDuplicateResults((prev) =>
          removeDuplicateFromResults(prev, duplicate.parentEntryId, duplicate.id),
        );

        toast.success(`已删除「${duplicate.title}」`);
        setDeleteDuplicate(null);
      })
      .catch(() => {
        toast.error("删除失败");
      });
  };

  return (
    <ListPageShell>
      <KnowledgeNav />
      <div className="mt-4">
        <PageHeader
          description={`审核 AI 提取的知识条目，通过或删除重复/不适合的内容 · 待审核 ${totalCount} 条`}
          title="审核中心"
        />
      </div>

      {entriesError !== null ? (
        <div className="mt-8 py-12 text-center text-destructive">
          <p>加载待审核条目失败</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {entriesError instanceof Error ? entriesError.message : String(entriesError)}
          </p>
          <Link className="mt-2 inline-block text-primary hover:underline" to="/knowledge">
            返回知识库
          </Link>
        </div>
      ) : pendingEntries.length === 0 && !isLoading ? (
        <div className="mt-8 py-12 text-center text-muted-foreground">
          <p>暂无待审核的条目</p>
          <Link className="mt-2 inline-block text-primary hover:underline" to="/knowledge">
            返回知识库
          </Link>
        </div>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
            {pendingEntries.map((entry) => {
              const duplicateResult = duplicateResults[entry.id];
              const isChecking = checkingDuplicates === entry.id;

              return (
                <Card className="flex flex-col" key={entry.id}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg leading-relaxed">
                      {/* Embedding 状态指示器 */}
                      <span
                        className={`inline-block h-2 w-2 shrink-0 rounded-full ${
                          entry.hasEmbedding ? "bg-success" : "bg-danger"
                        }`}
                        title={entry.hasEmbedding ? "已生成 embedding" : "尚未生成 embedding"}
                      />
                      <span className="flex-1">{entry.title}</span>
                    </CardTitle>
                    <CardDescription>
                      {entry.analysisDate && (
                        <span className="text-xs">
                          {unsafeDateTimeInputToUtcDate(entry.analysisDate).toLocaleDateString()}
                        </span>
                      )}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex-1 space-y-4">
                    {entry.sourceMessage && (
                      <div className="whitespace-pre-wrap text-muted-foreground text-sm">
                        {entry.sourceMessage}
                      </div>
                    )}

                    {/* 查重结果展示 */}
                    {duplicateResult && (
                      <div className="rounded-md border">
                        {duplicateResult.hasDuplicates ? (
                          <div className="space-y-3 p-4">
                            <div className="flex items-center gap-2 font-medium text-sm text-warning">
                              <AlertTriangle className="h-4 w-4" />
                              发现 {duplicateResult.duplicates.length} 条相似内容
                            </div>
                            <div className="space-y-2">
                              {duplicateResult.duplicates.map((dup) => {
                                const statusInfo = getStatusLabel(dup.status);
                                return (
                                  <div
                                    className="flex flex-col gap-3 rounded-md border bg-muted/50 p-3"
                                    key={dup.id}
                                  >
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="min-w-0 flex-1">
                                        <p className="font-medium text-sm">{dup.title}</p>
                                        <div className="mt-1 flex items-center gap-2">
                                          <Badge variant={statusInfo.variant}>
                                            {statusInfo.label}
                                          </Badge>
                                          <span className="text-muted-foreground text-xs">
                                            相似度: {Math.round(dup.score * 100)}%
                                          </span>
                                        </div>
                                      </div>
                                      <div className="flex shrink-0 gap-1">
                                        {/* 通过按钮：仅待审核状态显示 */}
                                        {dup.status === "pending_review" && (
                                          <Button
                                            onClick={() =>
                                              setApproveDuplicate({
                                                id: dup.id,
                                                title: dup.title,
                                                parentEntryId: entry.id,
                                              })
                                            }
                                            size="sm"
                                            title="通过此条目"
                                            variant="ghost"
                                          >
                                            <Check className="h-4 w-4" />
                                          </Button>
                                        )}
                                        {/* 删除按钮：所有状态都显示 */}
                                        <Button
                                          onClick={() =>
                                            setDeleteDuplicate({
                                              id: dup.id,
                                              title: dup.title,
                                              parentEntryId: entry.id,
                                            })
                                          }
                                          size="sm"
                                          title="删除此条目"
                                          variant="ghost"
                                        >
                                          <Trash2 className="h-4 w-4 text-destructive" />
                                        </Button>
                                      </div>
                                    </div>
                                    {dup.sourceMessage && (
                                      <div className="whitespace-pre-wrap text-muted-foreground text-xs">
                                        {dup.sourceMessage}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          <div className="p-4 text-center font-medium text-sm text-success">
                            ✓ 无重复内容
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                  <CardFooter className="flex gap-2">
                    <Button
                      disabled={isChecking || !entry.hasEmbedding}
                      onClick={() => handleCheckDuplicate(entry.id)}
                      size="sm"
                      title={entry.hasEmbedding ? "查重" : "尚未生成 embedding，无法查重"}
                      variant="outline"
                    >
                      {isChecking ? (
                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="mr-1 h-4 w-4" />
                      )}
                      查重
                    </Button>
                    <Button
                      onClick={() => setApproveEntry({ id: entry.id, title: entry.title })}
                      size="sm"
                      variant="default"
                    >
                      <Check className="mr-1 h-4 w-4" />
                      通过
                    </Button>
                    <Button
                      onClick={() => setDeleteEntry({ id: entry.id, title: entry.title })}
                      size="sm"
                      variant="destructive"
                    >
                      <Trash2 className="mr-1 h-4 w-4" />
                      删除
                    </Button>
                  </CardFooter>
                </Card>
              );
            })}
          </div>

          {/* Infinite scroll 触发器 */}
          <InfiniteScrollTrigger
            completedText={`已加载全部 ${totalCount} 条待审核条目`}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage ?? false}
            isFetchingNextPage={isFetchingNextPage}
            loadedCount={pendingEntries.length}
            totalCount={totalCount}
          />
        </>
      )}

      {/* 通过确认对话框 */}
      <AlertDialog onOpenChange={() => setApproveEntry(null)} open={!!approveEntry}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认审核通过</AlertDialogTitle>
            <AlertDialogDescription>
              确定要通过「{approveEntry?.title}」吗？通过后该条目将进入认领池。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleApprove}>确认通过</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 删除确认对话框 */}
      <AlertDialog onOpenChange={() => setDeleteEntry(null)} open={!!deleteEntry}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              确认删除
            </AlertDialogTitle>
            <AlertDialogDescription>
              确定要删除「{deleteEntry?.title}」吗？此操作不可恢复。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 相似条目通过确认对话框 */}
      <AlertDialog onOpenChange={() => setApproveDuplicate(null)} open={!!approveDuplicate}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认审核通过相似条目</AlertDialogTitle>
            <AlertDialogDescription>
              确定要通过相似条目「{approveDuplicate?.title}
              」吗？通过后该条目将进入认领池。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleApproveDuplicate}>确认通过</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 相似条目删除确认对话框 */}
      <AlertDialog onOpenChange={() => setDeleteDuplicate(null)} open={!!deleteDuplicate}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              确认删除相似条目
            </AlertDialogTitle>
            <AlertDialogDescription>
              确定要删除相似条目「{deleteDuplicate?.title}」吗？此操作不可恢复。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDeleteDuplicate}
            >
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ListPageShell>
  );
}
